import * as THREE from 'three';
import Meteor from './Meteor.js';

export default class MeteorManager {
    constructor(scene, physics, audioManager, bus = null) {
        console.log("[MeteorManager] Initializing");
        this.scene = scene;
        this.physics = physics;
        this.audioManager = audioManager;
        this.bus = bus;
        this.meteors = [];

        // Configuration
        this.config = {
            enabled: false, // Initially disabled, will be enabled in Game.start()
            minTimeBetweenEvents: 15,
            maxTimeBetweenEvents: 60,
            minMeteorsPerEvent: 3,
            maxMeteorsPerEvent: 7,
            nextEventTime: Number.MAX_SAFE_INTEGER,
            eventInProgress: false,
            warningTime: 2,
            playerAreaRadius: 200,
            minDistanceFromPlayer: 50,
            maxMeteorSize: 4.5,
            minMeteorSize: 0.8,
            spawnHeight: 200,
            maxMeteorsOnScreen: 15,
            busFocusChance: 0.5,
            directHitChance: 0.25,
            damageMultiplier: 0.7,
        };

        console.log(`[MeteorManager] Initialized with enabled=${this.config.enabled}, eventInProgress=${this.config.eventInProgress}`);

        // Warning effect — DOM overlay instead of 3D sky sphere
        this.warningSound = null;
        this.warningOverlay = null;

        // Runtime guards to keep meteor events smooth under load
        this.lastBusImpactTime = 0;
        this.busImpactCooldownMs = 1200;

        // Adaptive performance governor for meteor-heavy moments
        this.qualityLevel = 2; // 2=high, 1=medium, 0=low
        this.lastQualityChangeTime = 0;
        this.qualityCooldownMs = 1500;
        this.frameIndex = 0;
        this.pendingImpactTargets = [];
        this.accessibilityPreset = 'default';
        this.accessibilityPalettes = {
            default: {
                skyWarning: 0xff2200,
                direct: 0xff0000,
                near: 0xff6600,
                random: 0xff5500,
                explosionAir: 0xff6600,
                explosionGround: 0xffcc00
            },
            highContrast: {
                skyWarning: 0xffcc00,
                direct: 0x0033ff,
                near: 0x00d6ff,
                random: 0xff8800,
                explosionAir: 0x00d6ff,
                explosionGround: 0xfff200
            },
            deuteranopia: {
                skyWarning: 0xffbf00,
                direct: 0x0055ff,
                near: 0x00a4ff,
                random: 0xff8a00,
                explosionAir: 0x00b8ff,
                explosionGround: 0xffd45a
            }
        };

        // Explosion pool — managed in main update loop instead of independent rAF
        this.activeExplosions = [];
        this.maxConcurrentExplosions = 4;

        // Pre-allocated fragment geometry + material (reused across explosions)
        this.fragmentGeometry = new THREE.IcosahedronGeometry(1, 0);
        this.fragmentMaterial = new THREE.MeshStandardMaterial({
            color: 0xff4400,
            emissive: 0xff2200,
            emissiveIntensity: 0.5,
            transparent: true,
            opacity: 1
        });

        // Shared explosion particle material (ground + air variants)
        const palette = this.getAccessibilityPalette();
        this.explosionMaterialGround = new THREE.PointsMaterial({
            color: palette.explosionGround,
            size: 1.8,
            transparent: true,
            opacity: 0.9,
            blending: THREE.AdditiveBlending
        });
        this.explosionMaterialAir = new THREE.PointsMaterial({
            color: palette.explosionAir,
            size: 1.5,
            transparent: true,
            opacity: 0.9,
            blending: THREE.AdditiveBlending
        });

        // Max particles for the explosion pool buffer
        this.maxExplosionParticles = 60;

        // InstancedMesh setup — uses varied rocky shapes
        this.setupInstancedMesh();

        // Trail particles setup — actually used now
        this.setupTrailParticles();

        // Shadow InstancedMesh — batched ground shadows
        this.setupShadowInstances();
    }

    setupInstancedMesh() {
        // Use IcosahedronGeometry for a chunky rock look with fewer tris than SphereGeometry
        const radius = 2.0;
        this.meteorGeometry = new THREE.IcosahedronGeometry(radius, 1); // 80 tris vs 240 for sphere

        // Create meteor material with enhanced visual effects
        this.meteorMaterial = new THREE.MeshStandardMaterial({
            color: 0xff5500,
            emissive: 0xff2200,
            emissiveIntensity: 1.2, // Stronger self-glow replaces per-meteor PointLights
            roughness: 0.7,
            metalness: 0.5,
            flatShading: true,
            vertexColors: true
        });

        // Create instanced mesh with capacity for max meteors
        const maxInstances = this.config.maxMeteorsOnScreen;
        this.instancedMesh = new THREE.InstancedMesh(
            this.meteorGeometry,
            this.meteorMaterial,
            maxInstances
        );

        // Initialize instance colors with more variation and intensity
        const colors = new Float32Array(maxInstances * 3);
        for (let i = 0; i < maxInstances; i++) {
            const r = 1.0;
            const g = 0.2 + Math.random() * 0.3;
            const b = Math.random() * 0.2;
            colors[i * 3] = r;
            colors[i * 3 + 1] = g;
            colors[i * 3 + 2] = b;
        }
        this.instancedMesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);

        // Initially hide all instances
        this.instancedMesh.count = 0;
        this.instancedMesh.castShadow = true;
        this.instancedMesh.receiveShadow = false;
        this.instancedMesh.visible = true;
        this.scene.add(this.instancedMesh);

        // Create dummy object for matrix calculations
        this.dummy = new THREE.Object3D();

        // Track active instances
        this.activeInstances = 0;

        // Array to store meteor instance data
        this.meteorInstances = [];
    }

    setupShadowInstances() {
        // Batched InstancedMesh for ground target shadows instead of individual meshes
        const maxShadows = this.config.maxMeteorsOnScreen;
        this.shadowGeometry = new THREE.CircleGeometry(1, 8); // Unit circle, scaled per instance
        this.shadowMaterial = new THREE.MeshBasicMaterial({
            color: 0xff4400,
            transparent: true,
            opacity: 0.6,
            depthWrite: false
        });
        this.shadowInstancedMesh = new THREE.InstancedMesh(
            this.shadowGeometry,
            this.shadowMaterial,
            maxShadows
        );
        this.shadowInstancedMesh.count = 0;
        this.shadowInstancedMesh.rotation.x = -Math.PI / 2;
        this.shadowInstancedMesh.position.y = 0.1; // Base offset; per-instance terrain height added via Z
        this.shadowInstancedMesh.renderOrder = 1;
        this.scene.add(this.shadowInstancedMesh);

        // Per-instance color for shadows
        const shadowColors = new Float32Array(maxShadows * 3);
        this.shadowInstancedMesh.instanceColor = new THREE.InstancedBufferAttribute(shadowColors, 3);

        this.shadowDummy = new THREE.Object3D();
        this.activeShadowCount = 0;
    }

    setupTrailParticles() {
        // Trail particle pool — one Points object per meteor slot, reused
        const maxTrails = this.config.maxMeteorsOnScreen;
        const trailLength = 12; // particles per trail
        this.trailLength = trailLength;

        this.trailMaterial = new THREE.PointsMaterial({
            color: 0xff3300,
            size: 1.2,
            transparent: true,
            opacity: 0.8,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });

        // Pre-allocate trail buffers
        this.trailPool = [];
        for (let i = 0; i < maxTrails; i++) {
            const positions = new Float32Array(trailLength * 3);
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            const points = new THREE.Points(geometry, this.trailMaterial);
            points.frustumCulled = false;
            points.visible = false;
            this.scene.add(points);
            this.trailPool.push({ points, positions, geometry, head: 0 });
        }
    }

    resetNextEventTime() {
        console.log(`[MeteorManager] Resetting event timer. Previous state: eventInProgress=${this.config.eventInProgress}, meteorsActive=${this.meteorInstances.length}`);

        // Stop any in-progress event
        this.config.eventInProgress = false;
        this.config.meteorsSpawned = false;

        // Clear any existing meteors
        this.clear();

        // Set next event time to occur between min and max time
        const timeUntilNextEvent = this.config.minTimeBetweenEvents +
            Math.random() * (this.config.maxTimeBetweenEvents - this.config.minTimeBetweenEvents);

        this.config.nextEventTime = performance.now() / 1000 + timeUntilNextEvent;
        console.log(`[MeteorManager] Next event in ${timeUntilNextEvent.toFixed(1)} seconds`);

        // Remove warning overlay if it exists
        this.removeWarningOverlay();

        if (this.game?.minimapManager) {
            this.game.minimapManager.clearThreatZones();
        }
        if (this.game?.uiManager) {
            this.game.uiManager.clearMeteorCountdown();
        }
    }

    update(deltaTime) {
        if (!this.config.enabled) return;

        this.frameIndex++;
        this.updateQualityGovernor(deltaTime);

        const currentTime = performance.now() / 1000;

        // Debug: Log meteor system status
        if (this.debugTimer === undefined || currentTime > this.debugTimer + 5) {
            console.log(`[MeteorManager] Status: enabled=${this.config.enabled}, eventInProgress=${this.config.eventInProgress}, nextEventTime=${Math.round(this.config.nextEventTime - currentTime)}s, meteorsActive=${this.meteorInstances.length}`);
            this.debugTimer = currentTime;
        }

        // Check if it's time for a meteor event
        if (currentTime >= this.config.nextEventTime && !this.config.eventInProgress) {
            console.log(`[MeteorManager] Starting event at time ${currentTime}, nextEventTime was ${this.config.nextEventTime}`);
            this.startMeteorEvent();
        }

        // Update warning effect if event is about to start
        if (this.config.eventInProgress &&
            currentTime < this.config.eventWarningEndTime) {
            this.updateWarningEffect(currentTime);
        }

        // Spawn meteors if warning period is over
        if (this.config.eventInProgress &&
            currentTime >= this.config.eventWarningEndTime &&
            !this.config.meteorsSpawned) {
            console.log(`[MeteorManager] Spawning meteors at time ${currentTime}`);
            this.spawnMeteors();
            this.config.meteorsSpawned = true;
        }

        // Update existing meteors
        this.updateMeteors(deltaTime);

        // Update explosion pool in main loop
        this.updateExplosions(deltaTime);

        // Check if event is complete (all meteors gone)
        if (this.config.eventInProgress &&
            this.config.meteorsSpawned &&
            this.meteorInstances.length === 0) {
            console.log(`[MeteorManager] Event completed`);
            this.endMeteorEvent();
        }
    }

    updateQualityGovernor(deltaTime) {
        const frameMs = deltaTime * 1000;
        const now = performance.now();
        if (now - this.lastQualityChangeTime < this.qualityCooldownMs) return;

        // Degrade quickly on frame spikes.
        if (frameMs > 28 && this.qualityLevel > 0) {
            this.qualityLevel--;
            this.lastQualityChangeTime = now;
            return;
        }

        // Recover gradually when frame time is stable.
        if (frameMs < 18 && this.qualityLevel < 2) {
            this.qualityLevel++;
            this.lastQualityChangeTime = now;
        }
    }

    startMeteorEvent() {
        // Prevent auto-start events on page load
        const currentTime = performance.now() / 1000;
        if (currentTime < 5) {
            console.warn(`[MeteorManager] Prevented auto-start event triggered at ${currentTime.toFixed(1)}s after page load`);
            this.config.eventInProgress = false;
            this.config.nextEventTime = Number.MAX_SAFE_INTEGER;
            return;
        }

        if (!this.config.enabled) {
            console.log(`[MeteorManager] Attempted to start event but manager is disabled`);
            return;
        }

        console.log(`[MeteorManager] Starting meteor event!`);
        this.config.eventInProgress = true;
        this.config.meteorsSpawned = false;

        // Set when warning ends and meteors start spawning
        const warningEndTime = currentTime + this.config.warningTime;
        this.config.eventWarningEndTime = warningEndTime;
        console.log(`[MeteorManager] Warning ends at ${warningEndTime.toFixed(1)}, in ${this.config.warningTime}s`);

        // Begin warning effect — lightweight DOM overlay
        this.createWarningEffect();

        // Play warning sound
        if (this.audioManager) {
            this.audioManager.play('cube_explode');
        }

        // Determine number of meteors for this event
        this.config.meteorsInEvent = Math.floor(
            this.config.minMeteorsPerEvent +
            Math.random() * (this.config.maxMeteorsPerEvent - this.config.minMeteorsPerEvent + 1)
        );
        console.log(`[MeteorManager] Planning to spawn ${this.config.meteorsInEvent} meteors`);
        this.pendingImpactTargets = this.planImpactTargets(this.config.meteorsInEvent);

        if (this.game?.minimapManager) {
            this.game.minimapManager.setThreatZones(this.pendingImpactTargets);
        }
        if (this.game?.uiManager) {
            this.game.uiManager.showMeteorCountdown(this.config.warningTime, this.pendingImpactTargets.length);
        }
        window.dispatchEvent(new CustomEvent('meteor_warning_started', {
            detail: { count: this.pendingImpactTargets.length, duration: this.config.warningTime }
        }));

        // Show notification message
        if (this.game && this.game.showNotificationMessage) {
            this.game.showNotificationMessage('METEOR SHOWER INCOMING!', 'rgba(255, 60, 0, 0.8)');
        }
    }

    // DOM overlay instead of a 1000-unit 3D sky sphere
    createWarningEffect() {
        this.removeWarningOverlay();
        // Warning overlay removed — the big countdown number is the visual warning now
        this.warningOverlay = null;
    }

    removeWarningOverlay() {
        if (this.warningOverlay) {
            this.warningOverlay.remove();
            this.warningOverlay = null;
        }
    }

    updateWarningEffect(currentTime) {
        const remaining = Math.max(0, this.config.eventWarningEndTime - currentTime);
        if (this.game?.uiManager) {
            this.game.uiManager.updateMeteorCountdown(remaining, this.pendingImpactTargets.length);
        }
    }

    spawnMeteors() {
        if (!this.bus || !this.bus.mesh) {
            this.spawnMeteorsAtPosition({ x: 0, y: 0, z: 0 });
            return;
        }

        const busPosition = this.bus.mesh.position;
        this.spawnMeteorsAtPosition(busPosition);

        // Remove warning overlay
        this.removeWarningOverlay();

        if (this.game?.minimapManager) {
            this.game.minimapManager.clearThreatZones();
        }
        if (this.game?.uiManager) {
            this.game.uiManager.clearMeteorCountdown();
        }
    }

    spawnMeteorsAtPosition(position) {
        let totalMeteors = Math.min(
            this.config.meteorsInEvent,
            this.config.maxMeteorsOnScreen - this.meteorInstances.length
        );

        // Performance governor: reduce burst size on weaker frames.
        if (this.qualityLevel === 1) {
            totalMeteors = Math.min(totalMeteors, 5);
        } else if (this.qualityLevel === 0) {
            totalMeteors = Math.min(totalMeteors, 3);
        }

        if (totalMeteors <= 0) return;

        let spawned = 0;

        const spawnNextMeteor = () => {
            if (spawned >= totalMeteors) return;

            let x, z, distance;
            let attempts = 0;
            const maxAttempts = 10;

            let targetingType = this.determineTargetingType();
            const plannedTarget = this.pendingImpactTargets.shift();

            if (plannedTarget) {
                x = plannedTarget.x;
                z = plannedTarget.z;
                targetingType = plannedTarget.level;
                distance = Math.hypot(x - position.x, z - position.z);
            } else if (targetingType === 'direct' && this.bus && this.bus.mesh) {
                const offset = Math.random() * 3;
                x = this.bus.mesh.position.x + (Math.random() - 0.5) * offset;
                z = this.bus.mesh.position.z + (Math.random() - 0.5) * offset;
                distance = 0;
            } else if (targetingType === 'near' && this.bus && this.bus.mesh) {
                const nearRadius = 15 + Math.random() * 20;
                const angle = Math.random() * Math.PI * 2;
                x = this.bus.mesh.position.x + Math.cos(angle) * nearRadius;
                z = this.bus.mesh.position.z + Math.sin(angle) * nearRadius;
                distance = nearRadius;
            } else {
                do {
                    x = position.x + (Math.random() * 2 - 1) * this.config.playerAreaRadius;
                    z = position.z + (Math.random() * 2 - 1) * this.config.playerAreaRadius;
                    distance = Math.sqrt(
                        Math.pow(x - position.x, 2) +
                        Math.pow(z - position.z, 2)
                    );
                    attempts++;
                    if (attempts >= maxAttempts) {
                        x = position.x + (Math.random() * 2 - 1) * this.config.playerAreaRadius * 1.5;
                        z = position.z + (Math.random() * 2 - 1) * this.config.playerAreaRadius * 1.5;
                        break;
                    }
                } while (distance < this.config.minDistanceFromPlayer);
            }

            let sizeRandom = Math.random();
            sizeRandom = Math.pow(sizeRandom, 1.5);
            const size = this.config.minMeteorSize +
                sizeRandom * (this.config.maxMeteorSize - this.config.minMeteorSize);

            const speedFactor = 1.8 + (size / this.config.maxMeteorSize) * 1.2;
            const speed = (100 + Math.random() * 80) * speedFactor;
            const shadowSize = size * 2;

            try {
                this.createMeteorInstance({
                    position: { x, y: this.config.spawnHeight, z },
                    size,
                    speed,
                    targetingType,
                    shadowSize
                });

                spawned++;

                if (spawned < totalMeteors) {
                    let delay = 300 + Math.random() * 1000;
                    if (this.qualityLevel === 1) {
                        delay += 250;
                    } else if (this.qualityLevel === 0) {
                        delay += 450;
                    }
                    setTimeout(spawnNextMeteor, delay);
                }
            } catch (error) {
                console.error("Error creating meteor:", error);
                spawned++;
                if (spawned < totalMeteors) {
                    setTimeout(spawnNextMeteor, 500);
                }
            }
        };

        spawnNextMeteor();
    }

    planImpactTargets(count) {
        const targets = [];
        const center = this.bus?.mesh?.position || { x: 0, z: 0 };
        for (let i = 0; i < count; i++) {
            const level = this.determineTargetingType();
            let x = center.x;
            let z = center.z;
            if (level === 'direct') {
                const offset = Math.random() * 3;
                x += (Math.random() - 0.5) * offset;
                z += (Math.random() - 0.5) * offset;
            } else if (level === 'near') {
                const radius = 15 + Math.random() * 20;
                const angle = Math.random() * Math.PI * 2;
                x += Math.cos(angle) * radius;
                z += Math.sin(angle) * radius;
            } else {
                x += (Math.random() * 2 - 1) * this.config.playerAreaRadius;
                z += (Math.random() * 2 - 1) * this.config.playerAreaRadius;
            }
            targets.push({ x, z, radius: 12, level });
        }
        return targets;
    }

    determineTargetingType() {
        const roll = Math.random();
        if (roll < this.config.directHitChance) {
            return 'direct';
        } else if (roll < this.config.directHitChance + this.config.busFocusChance) {
            return 'near';
        } else {
            return 'random';
        }
    }

    getAccessibilityPalette() {
        return this.accessibilityPalettes[this.accessibilityPreset] || this.accessibilityPalettes.default;
    }

    setAccessibilityPreset(preset = 'default') {
        if (!this.accessibilityPalettes[preset]) return;
        this.accessibilityPreset = preset;
        // Update shared explosion materials with new palette
        const palette = this.getAccessibilityPalette();
        if (this.explosionMaterialGround) this.explosionMaterialGround.color.setHex(palette.explosionGround);
        if (this.explosionMaterialAir) this.explosionMaterialAir.color.setHex(palette.explosionAir);
    }

    getThreatColor(level) {
        const palette = this.getAccessibilityPalette();
        if (level === 'direct') return palette.direct;
        if (level === 'near') return palette.near;
        return palette.random;
    }

    createMeteorInstance(options) {
        const { position, size, speed, targetingType, shadowSize } = options;

        // Create physics body
        const body = this.physics.addSphere({
            radius: size * 2,
            position: position,
            mass: size * 10,
            restitution: 0.3,
            friction: 0.7,
            collisionFilterGroup: 0,
            collisionFilterMask: 0
        });

        const instanceIndex = this.activeInstances;
        const meteorInstance = {
            index: instanceIndex,
            position: { ...position },
            size: size,
            speed: speed,
            body: body,
            age: 0,
            lifespan: 10,
            exploded: false,
            light: null,
            targetingType: targetingType,
            shadowSize: shadowSize,
            shadowIndex: -1,
            trailIndex: -1,
            // Per-meteor rotation for tumble effect
            rotation: new THREE.Euler(
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2
            ),
            rotationSpeed: new THREE.Vector3(
                (Math.random() - 0.5) * 4,
                (Math.random() - 0.5) * 4,
                (Math.random() - 0.5) * 3
            )
        };

        // Only create PointLight for "direct" targeting (max 2-3 lights at once)
        if (targetingType === 'direct') {
            const lightColor = this.getThreatColor('direct');
            const light = new THREE.PointLight(lightColor, 2.0, 30);
            light.position.set(position.x, position.y, position.z);
            this.scene.add(light);
            meteorInstance.light = light;
        }

        // Add shadow via batched InstancedMesh
        if (targetingType === 'direct' || targetingType === 'near') {
            const shadowIdx = this.activeShadowCount;
            meteorInstance.shadowIndex = shadowIdx;

            // Set shadow color
            const shadowColor = new THREE.Color(this.getThreatColor(targetingType));
            this.shadowInstancedMesh.instanceColor.setXYZ(shadowIdx, shadowColor.r, shadowColor.g, shadowColor.b);
            this.shadowInstancedMesh.instanceColor.needsUpdate = true;

            // Position and scale shadow (in XZ plane since parent is rotated)
            this.shadowDummy.position.set(position.x, position.z, 0);
            this.shadowDummy.scale.set(shadowSize, shadowSize, 1);
            this.shadowDummy.updateMatrix();
            this.shadowInstancedMesh.setMatrixAt(shadowIdx, this.shadowDummy.matrix);

            this.activeShadowCount++;
            this.shadowInstancedMesh.count = this.activeShadowCount;
            this.shadowInstancedMesh.instanceMatrix.needsUpdate = true;
        }

        // Assign a trail from the pool
        if (instanceIndex < this.trailPool.length) {
            meteorInstance.trailIndex = instanceIndex;
            const trail = this.trailPool[instanceIndex];
            trail.points.visible = true;
            trail.head = 0;
            // Initialize all trail positions to spawn point
            for (let j = 0; j < this.trailLength * 3; j += 3) {
                trail.positions[j] = position.x;
                trail.positions[j + 1] = position.y;
                trail.positions[j + 2] = position.z;
            }
            trail.geometry.attributes.position.needsUpdate = true;
        }

        // Add to instances array
        this.meteorInstances.push(meteorInstance);

        // Update instance matrix with rotation
        this.dummy.position.set(position.x, position.y, position.z);
        this.dummy.rotation.copy(meteorInstance.rotation);
        this.dummy.scale.set(size, size, size);
        this.dummy.updateMatrix();
        this.instancedMesh.setMatrixAt(instanceIndex, this.dummy.matrix);

        // Update instance count
        this.activeInstances++;
        this.instancedMesh.count = this.activeInstances;
        this.instancedMesh.instanceMatrix.needsUpdate = true;

        return meteorInstance;
    }

    updateMeteors(deltaTime) {
        if (this.meteorInstances.length === 0) return;

        let needsMatrixUpdate = false;
        let needsShadowUpdate = false;

        for (let i = this.meteorInstances.length - 1; i >= 0; i--) {
            const meteor = this.meteorInstances[i];

            try {
                meteor.age += deltaTime;

                if (meteor.age > meteor.lifespan) {
                    this.removeMeteor(i);
                    needsMatrixUpdate = true;
                    continue;
                }

                if (meteor.exploded) continue;

                if (meteor.body) {
                    // Apply velocity with slight randomness
                    const vx = (Math.random() - 0.5) * 3;
                    const vy = -meteor.speed;
                    const vz = (Math.random() - 0.5) * 3;

                    meteor.body.setLinearVelocity({ x: vx, y: vy, z: vz });

                    const position = meteor.body.getPosition();
                    meteor.position.x = position.x;
                    meteor.position.y = position.y;
                    meteor.position.z = position.z;

                    // Update tumble rotation
                    meteor.rotation.x += meteor.rotationSpeed.x * deltaTime;
                    meteor.rotation.y += meteor.rotationSpeed.y * deltaTime;
                    meteor.rotation.z += meteor.rotationSpeed.z * deltaTime;

                    // Update instance matrix with rotation
                    this.dummy.position.set(position.x, position.y, position.z);
                    this.dummy.rotation.set(meteor.rotation.x, meteor.rotation.y, meteor.rotation.z);
                    this.dummy.scale.set(meteor.size, meteor.size, meteor.size);
                    this.dummy.updateMatrix();
                    this.instancedMesh.setMatrixAt(meteor.index, this.dummy.matrix);
                    needsMatrixUpdate = true;

                    // Update light position (only direct-hit meteors have lights)
                    if (meteor.light) {
                        meteor.light.position.copy(position);
                    }

                    // Update trail particles
                    if (meteor.trailIndex >= 0 && meteor.trailIndex < this.trailPool.length) {
                        const trail = this.trailPool[meteor.trailIndex];
                        // Shift trail: move all positions forward, add new head position
                        for (let j = (this.trailLength - 1) * 3; j >= 3; j -= 3) {
                            trail.positions[j] = trail.positions[j - 3];
                            trail.positions[j + 1] = trail.positions[j - 2];
                            trail.positions[j + 2] = trail.positions[j - 1];
                        }
                        // Add slight random spread to trail head for fiery look
                        trail.positions[0] = position.x + (Math.random() - 0.5) * meteor.size * 0.5;
                        trail.positions[1] = position.y + (Math.random() - 0.5) * meteor.size * 0.3;
                        trail.positions[2] = position.z + (Math.random() - 0.5) * meteor.size * 0.5;
                        trail.geometry.attributes.position.needsUpdate = true;
                    }

                    // Update batched shadow
                    if (meteor.shadowIndex >= 0 && (this.qualityLevel > 0 || this.frameIndex % 2 === 0)) {
                        const shadowHeightMap = this.scene?.userData?.terrainHeightMap;
                        const shadowTerrainY = shadowHeightMap ? shadowHeightMap.getHeight(position.x, position.z) : 0;
                        const distanceToGround = Math.max(0, position.y - shadowTerrainY - 1);
                        const maxDistance = this.config.spawnHeight - 1;
                        const opacityFactor = 1 - (distanceToGround / maxDistance);

                        // Scale shadow based on proximity (grows as meteor gets closer)
                        const scaleFactor = 0.5 + opacityFactor * 0.5;
                        const ss = meteor.shadowSize * scaleFactor;

                        // Z in local space maps to Y in world space (due to -PI/2 rotation)
                        this.shadowDummy.position.set(position.x, position.z, shadowTerrainY);
                        this.shadowDummy.scale.set(ss, ss, 1);
                        this.shadowDummy.updateMatrix();
                        this.shadowInstancedMesh.setMatrixAt(meteor.shadowIndex, this.shadowDummy.matrix);

                        // Pulse direct-hit shadows
                        let alpha = opacityFactor;
                        if (meteor.targetingType === 'direct') {
                            const pulse = Math.sin(meteor.age * 5) * 0.3 + 0.7;
                            alpha *= pulse;
                        }
                        // Encode opacity into color brightness
                        const baseColor = new THREE.Color(this.getThreatColor(meteor.targetingType));
                        baseColor.multiplyScalar(alpha);
                        this.shadowInstancedMesh.instanceColor.setXYZ(meteor.shadowIndex, baseColor.r, baseColor.g, baseColor.b);

                        needsShadowUpdate = true;
                    }

                    // Check for collision with the bus
                    if (this.bus && this.bus.mesh && !meteor.exploded) {
                        this.checkBusCollision(meteor, position);
                    }

                    // Check if meteor hit the ground (terrain-aware)
                    const groundHeightMap = this.scene?.userData?.terrainHeightMap;
                    const groundTerrainY = groundHeightMap ? groundHeightMap.getHeight(position.x, position.z) : 0;
                    if (position.y < groundTerrainY + 0.5 && !meteor.exploded) {
                        this.explodeMeteor(i, true);
                        needsMatrixUpdate = true;
                    }
                }
            } catch (error) {
                console.error("Error updating meteor:", error);
                this.removeMeteor(i);
                needsMatrixUpdate = true;
            }
        }

        if (needsMatrixUpdate) {
            this.instancedMesh.instanceMatrix.needsUpdate = true;
        }
        if (needsShadowUpdate) {
            this.shadowInstancedMesh.instanceMatrix.needsUpdate = true;
            this.shadowInstancedMesh.instanceColor.needsUpdate = true;
        }
    }

    checkBusCollision(meteor, meteorPosition) {
        if (!this.bus || !this.bus.mesh) return;

        const busPosition = this.bus.mesh.position;
        const busSize = {
            width: this.bus.width || 2.5,
            height: this.bus.height || 3,
            length: this.bus.length || 12
        };

        const busBox = {
            minX: busPosition.x - busSize.width / 2,
            maxX: busPosition.x + busSize.width / 2,
            minY: busPosition.y,
            maxY: busPosition.y + busSize.height,
            minZ: busPosition.z - busSize.length / 2,
            maxZ: busPosition.z + busSize.length / 2
        };

        const meteorRadius = meteor.size * 2;
        const meteorBox = {
            minX: meteorPosition.x - meteorRadius,
            maxX: meteorPosition.x + meteorRadius,
            minY: meteorPosition.y - meteorRadius,
            maxY: meteorPosition.y + meteorRadius,
            minZ: meteorPosition.z - meteorRadius,
            maxZ: meteorPosition.z + meteorRadius
        };

        if (meteorBox.minX <= busBox.maxX && meteorBox.maxX >= busBox.minX &&
            meteorBox.minY <= busBox.maxY && meteorBox.maxY >= busBox.minY &&
            meteorBox.minZ <= busBox.maxZ && meteorBox.maxZ >= busBox.minZ) {

            this.handleBusImpact(meteor);

            const meteorIndex = this.meteorInstances.indexOf(meteor);
            if (meteorIndex !== -1) {
                this.explodeMeteor(meteorIndex);
            }

            return true;
        }

        return false;
    }

    handleBusImpact(meteor) {
        const now = performance.now();
        if (now - this.lastBusImpactTime < this.busImpactCooldownMs) {
            return;
        }
        this.lastBusImpactTime = now;

        const speedFactor = meteor.speed / 180;
        const damage = meteor.size * 15 * this.config.damageMultiplier * speedFactor;

        if (this.bus && typeof this.bus.damage === 'function') {
            this.bus.damage(damage);
        }

        if (this.bus && this.bus.fuel !== undefined) {
            const currentFuel = this.bus.fuel;
            const newFuel = currentFuel * 0.5;
            this.bus.fuel = newFuel;

            if (window && typeof window.dispatchEvent === 'function') {
                const fuelEvent = new CustomEvent('fuel_updated', {
                    detail: {
                        level: newFuel,
                        capacity: this.bus.fuelCapacity || 100,
                        percentage: newFuel / (this.bus.fuelCapacity || 100) * 100,
                        empty: newFuel <= 0
                    }
                });
                window.dispatchEvent(fuelEvent);
            }

            if (this.game && this.game.showNotificationMessage) {
                this.game.showNotificationMessage('METEOR IMPACT! 50% FUEL LOST!', 'rgba(255, 60, 0, 0.9)');
            }
        }

        if (this.game && typeof this.game.applyCameraShake === 'function') {
            try {
                const shakeIntensity = Math.min(8, meteor.size * 3 * speedFactor);
                const shakeDuration = Math.min(1.5, 0.6 + meteor.size * 0.4);
                this.game.applyCameraShake(shakeIntensity, shakeDuration);
            } catch (error) {
                console.error("Error applying camera shake:", error);
            }
        }

        if (this.game?.uiManager) {
            this.game.uiManager.showDamageVignette(Math.min(1, 0.5 + meteor.size * 0.12));
            this.game.uiManager.pulseFuelGauge();
        }
        window.dispatchEvent(new CustomEvent('meteor_bus_impact'));

        if (this.audioManager) {
            this.audioManager.playSoundEffect('cube_explode', 1.0);
            setTimeout(() => {
                this.audioManager.playSoundEffect('collision_heavy', 0.8);
            }, 100);
        }

        const explosionSizeFactor = 2.5;
        this.createExplosion(meteor.position, meteor.size * explosionSizeFactor);
    }

    explodeMeteor(index, isGroundImpact = false) {
        const meteor = this.meteorInstances[index];
        if (!meteor || meteor.exploded) return;

        meteor.exploded = true;

        if (this.audioManager) {
            const soundEffect = 'cube_explode';
            const volume = isGroundImpact ? 0.8 + meteor.size * 0.1 : 0.7;
            this.audioManager.playSoundEffect(soundEffect, volume);
        }

        const explosionSize = isGroundImpact ? meteor.size * 1.5 : meteor.size;
        this.createExplosion(meteor.position, explosionSize, isGroundImpact);

        // Lower threshold: shake for any meteor size > 1.5 (was 2.5)
        if (isGroundImpact && meteor.size > 1.5 && this.game && this.game.applyCameraShake) {
            try {
                const distanceToBus = this.bus && this.bus.mesh ?
                    Math.hypot(
                        meteor.position.x - this.bus.mesh.position.x,
                        meteor.position.y - this.bus.mesh.position.y,
                        meteor.position.z - this.bus.mesh.position.z
                    ) : 100;

                const distanceFactor = Math.max(0.1, Math.min(1, 50 / distanceToBus));
                const shakeIntensity = meteor.size * 1.0 * distanceFactor;
                const shakeDuration = 0.3 + meteor.size * 0.12;

                if (shakeIntensity > 0.3) {
                    this.game.applyCameraShake(shakeIntensity, shakeDuration);
                }
            } catch (error) {
                console.error("Error applying ground impact camera shake:", error);
            }
        }

        // Remove meteor after a short delay
        setTimeout(() => {
            const currentIndex = this.meteorInstances.indexOf(meteor);
            if (currentIndex !== -1) {
                this.removeMeteor(currentIndex);
            }
            this.instancedMesh.instanceMatrix.needsUpdate = true;
        }, 100);
    }

    removeMeteor(index) {
        if (index < 0 || index >= this.meteorInstances.length) return;

        const meteor = this.meteorInstances[index];

        // Remove physics body
        if (meteor.body && this.physics) {
            try {
                this.physics.removeBody(meteor.body);
            } catch (error) {
                console.error("Error removing physics body:", error);
            }
        }

        // Remove light (only direct-hit meteors have one)
        if (meteor.light) {
            this.scene.remove(meteor.light);
        }

        // Hide trail
        if (meteor.trailIndex >= 0 && meteor.trailIndex < this.trailPool.length) {
            this.trailPool[meteor.trailIndex].points.visible = false;
        }

        // Handle batched shadow removal — swap-with-last for shadow indices too
        if (meteor.shadowIndex >= 0) {
            const lastShadowIdx = this.activeShadowCount - 1;
            if (meteor.shadowIndex !== lastShadowIdx) {
                // Find the meteor that owns the last shadow slot and reassign
                for (const m of this.meteorInstances) {
                    if (m.shadowIndex === lastShadowIdx) {
                        // Copy last shadow's matrix into the removed slot
                        const mat = new THREE.Matrix4();
                        this.shadowInstancedMesh.getMatrixAt(lastShadowIdx, mat);
                        this.shadowInstancedMesh.setMatrixAt(meteor.shadowIndex, mat);
                        // Copy color
                        const r = this.shadowInstancedMesh.instanceColor.getX(lastShadowIdx);
                        const g = this.shadowInstancedMesh.instanceColor.getY(lastShadowIdx);
                        const b = this.shadowInstancedMesh.instanceColor.getZ(lastShadowIdx);
                        this.shadowInstancedMesh.instanceColor.setXYZ(meteor.shadowIndex, r, g, b);
                        m.shadowIndex = meteor.shadowIndex;
                        break;
                    }
                }
            }
            this.activeShadowCount--;
            this.shadowInstancedMesh.count = this.activeShadowCount;
            this.shadowInstancedMesh.instanceMatrix.needsUpdate = true;
            this.shadowInstancedMesh.instanceColor.needsUpdate = true;
        }

        // O(1) removal: swap with last active meteor and pop.
        const lastIndex = this.meteorInstances.length - 1;
        if (index !== lastIndex) {
            const lastMeteor = this.meteorInstances[lastIndex];
            this.meteorInstances[index] = lastMeteor;
            lastMeteor.index = index;

            // Swap trail assignment
            if (lastMeteor.trailIndex === lastIndex && lastMeteor.trailIndex < this.trailPool.length) {
                // Trail index follows the meteor, no swap needed since pool is index-based
            }

            this.dummy.position.set(lastMeteor.position.x, lastMeteor.position.y, lastMeteor.position.z);
            this.dummy.rotation.set(lastMeteor.rotation.x, lastMeteor.rotation.y, lastMeteor.rotation.z);
            this.dummy.scale.set(lastMeteor.size, lastMeteor.size, lastMeteor.size);
            this.dummy.updateMatrix();
            this.instancedMesh.setMatrixAt(index, this.dummy.matrix);
        }

        this.meteorInstances.pop();
        this.instancedMesh.setMatrixAt(lastIndex, new THREE.Matrix4());

        this.activeInstances = this.meteorInstances.length;
        this.instancedMesh.count = this.activeInstances;
        this.instancedMesh.instanceMatrix.needsUpdate = true;
    }

    // Explosion pool — managed in main update loop
    createExplosion(position, size, isGroundImpact = false) {
        const qualityExplosionCap = this.qualityLevel === 2 ? this.maxConcurrentExplosions :
            this.qualityLevel === 1 ? 3 : 2;
        const isOverloaded = this.activeExplosions.length >= qualityExplosionCap;

        const particleCount = isOverloaded
            ? (isGroundImpact ? 22 : 16)
            : this.qualityLevel === 2
                ? (isGroundImpact ? 60 : 45)
                : this.qualityLevel === 1
                    ? (isGroundImpact ? 40 : 30)
                    : (isGroundImpact ? 28 : 20);

        const explosionGeometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        for (let i = 0; i < particleCount * 3; i += 3) {
            positions[i] = position.x;
            positions[i + 1] = position.y;
            positions[i + 2] = position.z;
        }
        explosionGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Clone shared material so we can fade this explosion independently
        const baseMaterial = isGroundImpact ? this.explosionMaterialGround : this.explosionMaterialAir;
        const explosionMaterial = baseMaterial.clone();
        explosionMaterial.opacity = 0.9;

        const explosion = new THREE.Points(explosionGeometry, explosionMaterial);
        this.scene.add(explosion);

        const velocityMultiplier = isOverloaded
            ? (isGroundImpact ? 18 : 14)
            : (isGroundImpact ? 22 : 18);
        const velocities = [];
        for (let i = 0; i < particleCount; i++) {
            const yBias = isGroundImpact ? Math.random() * 0.8 + 0.2 : Math.random() - 0.5;
            velocities.push({
                x: (Math.random() - 0.5) * velocityMultiplier,
                y: yBias * velocityMultiplier,
                z: (Math.random() - 0.5) * velocityMultiplier
            });
        }

        const flashIntensity = isGroundImpact ? 5 : 3.5;
        const flashRadius = isGroundImpact ? 50 : 40;
        const flash = new THREE.PointLight(0xffaa00, flashIntensity, flashRadius);
        flash.position.copy(position);
        this.scene.add(flash);

        let crater = null;
        let shockwave = null;

        // Fragments reuse shared geometry
        const fragments = [];
        if (!isOverloaded && this.qualityLevel > 0 && (isGroundImpact || Math.random() > 0.5)) {
            const fragmentCount = Math.min(8, Math.floor(size * 2));
            const fragmentSize = size / 3;

            for (let i = 0; i < fragmentCount; i++) {
                const material = this.fragmentMaterial.clone();
                material.opacity = 1;
                const fragment = new THREE.Mesh(this.fragmentGeometry, material);
                const s = fragmentSize * (0.5 + Math.random() * 0.5);
                fragment.scale.set(s, s, s);
                fragment.position.copy(position);
                fragment.rotation.set(
                    Math.random() * Math.PI * 2,
                    Math.random() * Math.PI * 2,
                    Math.random() * Math.PI * 2
                );

                const yBias = isGroundImpact ? 1.5 : 0.5;
                fragment.velocity = new THREE.Vector3(
                    (Math.random() - 0.5) * 10,
                    (Math.random() * 6 + 2) * yBias,
                    (Math.random() - 0.5) * 10
                );
                fragment.rotationSpeed = new THREE.Vector3(
                    (Math.random() - 0.5) * 0.2,
                    (Math.random() - 0.5) * 0.2,
                    (Math.random() - 0.5) * 0.2
                );

                this.scene.add(fragment);
                fragments.push(fragment);
            }
        }

        if (isGroundImpact) {
            const craterSize = size * 3;
            const craterGeometry = new THREE.CircleGeometry(craterSize, 16);
            const craterMaterial = new THREE.MeshBasicMaterial({
                color: 0x222222,
                transparent: true,
                opacity: 0.8
            });
            crater = new THREE.Mesh(craterGeometry, craterMaterial);
            crater.rotation.x = -Math.PI / 2;
            const craterHeightMap = this.scene?.userData?.terrainHeightMap;
            const craterTerrainY = craterHeightMap ? craterHeightMap.getHeight(position.x, position.z) : 0;
            crater.position.set(position.x, craterTerrainY + 0.01, position.z);
            this.scene.add(crater);

            const shockwaveGeometry = new THREE.RingGeometry(0.1, 0.2, 16);
            const shockwaveMaterial = new THREE.MeshBasicMaterial({
                color: 0xff8800,
                transparent: true,
                opacity: 0.9,
                side: THREE.DoubleSide
            });
            shockwave = new THREE.Mesh(shockwaveGeometry, shockwaveMaterial);
            shockwave.rotation.x = -Math.PI / 2;
            shockwave.position.set(position.x, craterTerrainY + 0.02, position.z);
            this.scene.add(shockwave);
        }

        // Store explosion data for main-loop update
        this.activeExplosions.push({
            time: 0,
            explosion,
            explosionMaterial,
            flash,
            flashIntensity,
            crater,
            shockwave,
            fragments,
            velocities,
            particleCount,
            size,
            isGroundImpact
        });
    }

    // Called from update() — replaces independent requestAnimationFrame per explosion
    updateExplosions(deltaTime) {
        for (let i = this.activeExplosions.length - 1; i >= 0; i--) {
            const ex = this.activeExplosions[i];
            ex.time += deltaTime;

            const t = ex.time;

            // Animate particles
            const positions = ex.explosion.geometry.attributes.position.array;
            for (let j = 0; j < ex.particleCount; j++) {
                const idx = j * 3;
                positions[idx] += ex.velocities[j].x * deltaTime * 5;
                positions[idx + 1] += ex.velocities[j].y * deltaTime * 5;
                positions[idx + 2] += ex.velocities[j].z * deltaTime * 5;
                if (ex.isGroundImpact) {
                    ex.velocities[j].y -= 1.5 * deltaTime;
                }
            }
            ex.explosion.geometry.attributes.position.needsUpdate = true;

            // Fade particles
            ex.explosionMaterial.opacity = Math.max(0, 0.9 * (1 - t / 1.5));

            // Fade flash light
            if (ex.flash) {
                ex.flash.intensity = Math.max(0, ex.flashIntensity * (1 - t / 0.4));
            }

            // Animate shockwave
            if (ex.isGroundImpact && ex.shockwave) {
                const maxSize = ex.size * 12;
                const currentSize = Math.min(maxSize, t * maxSize * 2);
                ex.shockwave.scale.set(currentSize, currentSize, 1);
                ex.shockwave.material.opacity = Math.max(0, 0.9 * (1 - t / 1.2));
            }

            // Animate fragments
            for (const fragment of ex.fragments) {
                fragment.position.x += fragment.velocity.x * deltaTime * 5;
                fragment.position.y += fragment.velocity.y * deltaTime * 5;
                fragment.position.z += fragment.velocity.z * deltaTime * 5;
                fragment.velocity.y -= 7.5 * deltaTime;
                fragment.rotation.x += fragment.rotationSpeed.x;
                fragment.rotation.y += fragment.rotationSpeed.y;
                fragment.rotation.z += fragment.rotationSpeed.z;
                if (fragment.material.opacity > 0.01) {
                    fragment.material.opacity = Math.max(0, fragment.material.opacity - deltaTime * 0.5);
                }
            }

            // Cleanup when done
            if (t >= 1.5) {
                this.scene.remove(ex.explosion);
                if (ex.flash) this.scene.remove(ex.flash);
                ex.explosion.geometry.dispose();
                ex.explosionMaterial.dispose();

                for (const fragment of ex.fragments) {
                    this.scene.remove(fragment);
                    // Don't dispose shared fragmentGeometry — only dispose cloned material
                    fragment.material.dispose();
                }

                if (ex.crater) {
                    setTimeout(() => {
                        this.scene.remove(ex.crater);
                        ex.crater.geometry.dispose();
                        ex.crater.material.dispose();
                    }, 5000);
                }

                if (ex.shockwave) {
                    this.scene.remove(ex.shockwave);
                    ex.shockwave.geometry.dispose();
                    ex.shockwave.material.dispose();
                }

                this.activeExplosions.splice(i, 1);
            }
        }
    }

    endMeteorEvent() {
        this.config.eventInProgress = false;
        this.pendingImpactTargets = [];
        if (this.game?.minimapManager) {
            this.game.minimapManager.clearThreatZones();
        }
        if (this.game?.uiManager) {
            this.game.uiManager.clearMeteorCountdown();
        }
        this.resetNextEventTime();
    }

    setBus(bus) {
        this.bus = bus;
    }

    setGame(game) {
        this.game = game;
    }

    setEnabled(enabled) {
        console.log(`[MeteorManager] Setting enabled=${enabled} (was ${this.config.enabled})`);
        this.config.enabled = enabled;
    }

    // For external triggering (e.g., for testing or by other game events)
    triggerMeteorEvent() {
        if (!this.config.eventInProgress) {
            this.config.meteorsInEvent = 5;
            this.startMeteorEvent();
            return true;
        }
        return false;
    }

    clear() {
        this.pendingImpactTargets = [];

        // Remove all meteors
        for (let i = this.meteorInstances.length - 1; i >= 0; i--) {
            this.removeMeteor(i);
        }

        // Reset instanced mesh
        this.instancedMesh.count = 0;
        this.instancedMesh.instanceMatrix.needsUpdate = true;

        // Reset shadow instances
        this.activeShadowCount = 0;
        this.shadowInstancedMesh.count = 0;
        this.shadowInstancedMesh.instanceMatrix.needsUpdate = true;

        // Hide all trails
        for (const trail of this.trailPool) {
            trail.points.visible = false;
        }

        // Clean up active explosions
        for (const ex of this.activeExplosions) {
            this.scene.remove(ex.explosion);
            if (ex.flash) this.scene.remove(ex.flash);
            ex.explosion.geometry.dispose();
            ex.explosionMaterial.dispose();
            for (const fragment of ex.fragments) {
                this.scene.remove(fragment);
                fragment.material.dispose();
            }
            if (ex.crater) {
                this.scene.remove(ex.crater);
                ex.crater.geometry.dispose();
                ex.crater.material.dispose();
            }
            if (ex.shockwave) {
                this.scene.remove(ex.shockwave);
                ex.shockwave.geometry.dispose();
                ex.shockwave.material.dispose();
            }
        }
        this.activeExplosions = [];

        // Remove warning overlay
        this.removeWarningOverlay();

        if (this.game?.minimapManager) {
            this.game.minimapManager.clearThreatZones();
        }
        if (this.game?.uiManager) {
            this.game.uiManager.clearMeteorCountdown();
        }
    }

    dispose() {
        this.clear();

        if (this.instancedMesh) {
            this.scene.remove(this.instancedMesh);
            this.instancedMesh.geometry.dispose();
            this.instancedMesh.material.dispose();
            this.instancedMesh = null;
        }

        if (this.shadowInstancedMesh) {
            this.scene.remove(this.shadowInstancedMesh);
            this.shadowInstancedMesh.geometry.dispose();
            this.shadowInstancedMesh.material.dispose();
            this.shadowInstancedMesh = null;
        }

        // Dispose trail pool
        for (const trail of this.trailPool) {
            this.scene.remove(trail.points);
            trail.geometry.dispose();
        }
        this.trailPool = [];

        if (this.meteorGeometry) this.meteorGeometry.dispose();
        if (this.meteorMaterial) this.meteorMaterial.dispose();
        if (this.shadowGeometry) this.shadowGeometry.dispose();
        if (this.shadowMaterial) this.shadowMaterial.dispose();
        if (this.trailMaterial) this.trailMaterial.dispose();
        if (this.fragmentGeometry) this.fragmentGeometry.dispose();
        if (this.fragmentMaterial) this.fragmentMaterial.dispose();
        if (this.explosionMaterialGround) this.explosionMaterialGround.dispose();
        if (this.explosionMaterialAir) this.explosionMaterialAir.dispose();
    }
}
